import React from "react";
import { logActivity } from "../utils/activity";
import { NavLink } from "react-router-dom";
import siteData from "../data/site.json";
import legalData from "../data/legal.json";
import refundData from "../data/refundPolicy.json";

export default function Footer() {
  const linkMap = {
    Home: "/",
    About: "/about",
    Features: "/features",
    Pricing: "/pricing",
    "How It Works": "/how-it-works",
    "Use Cases": "/use-cases",
    Blog: "/blog",
    FAQ: "/faq",
    Contact: "/contact",
    Support: "/support",
  };
  const quickLinks = siteData.footer?.quickLinks || [];
  const perColumn = Math.ceil(quickLinks.length / 3) || 1;
  const columns = Array.from({ length: 3 }, (_, index) =>
    quickLinks.slice(index * perColumn, (index + 1) * perColumn)
  );
  const socialIcons = {
    LinkedIn: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
        <path
          fill="currentColor"
          d="M4.98 3.5C4.98 4.88 3.88 6 2.5 6C1.12 6 0 4.88 0 3.5C0 2.12 1.12 1 2.5 1C3.88 1 4.98 2.12 4.98 3.5ZM0.5 8H4.5V23H0.5V8ZM8.5 8H12.3V10.05H12.35C12.88 9.05 14.15 8 16.05 8C20.12 8 21 10.53 21 15.17V23H17V15.96C17 14.31 16.97 12.2 14.72 12.2C12.45 12.2 12.12 13.88 12.12 15.85V23H8.5V8Z"
        />
      </svg>
    ),
    Twitter: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
        <path
          fill="currentColor"
          d="M18.9 2H22.6L14.6 11.1L24 22H16.9L11.4 15.2L5.4 22H1.4L9.9 12.3L0.9 2H8.2L13.1 8L18.9 2ZM17.6 19.8H19.6L7 4.1H4.8L17.6 19.8Z"
        />
      </svg>
    ),
    YouTube: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
        <path
          fill="currentColor"
          d="M23.5 7.2C23.2 6 22.2 5.1 21 4.8C18.8 4.2 12 4.2 12 4.2C12 4.2 5.2 4.2 3 4.8C1.8 5.1 0.8 6 0.5 7.2C0 9.5 0 12 0 12C0 12 0 14.5 0.5 16.8C0.8 18 1.8 18.9 3 19.2C5.2 19.8 12 19.8 12 19.8C12 19.8 18.8 19.8 21 19.2C22.2 18.9 23.2 18 23.5 16.8C24 14.5 24 12 24 12C24 12 24 9.5 23.5 7.2ZM9.6 15.2V8.8L15.4 12L9.6 15.2Z"
        />
      </svg>
    ),
  };

  return (
    <footer className="border-t border-slate-200 bg-white">
      <div className="mx-auto grid w-full max-w-6xl gap-10 px-4 py-12 md:grid-cols-[1.4fr_1fr_1fr] md:px-6">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500 text-white">
              {siteData.brand?.short || "MYV"}
            </div>
            <div>
              <p className="text-lg font-semibold text-slate-900">
                {`${siteData.brand?.name || "Manage Your Vastu"} (${
                  siteData.brand?.short || "MYV"
                })`}
              </p>
              <p className="text-sm text-slate-500">
                {siteData.defaultDescription}
              </p>
            </div>
          </div>
          <p className="text-sm text-slate-500">
            {siteData.defaultDescription}
          </p>
          <div className="flex flex-wrap gap-4 text-sm text-slate-600">
            {siteData.footer?.social?.map((item) => (
              <a
                key={item.name}
                href={item.url}
                className="inline-flex items-center gap-2 transition hover:text-amber-600"
                onClick={() =>
                  logActivity("social_click", { label: item.name })
                }
              >
                {socialIcons[item.name]}
                <span>{item.name}</span>
              </a>
            ))}
          </div>
        </div>

        <div className="space-y-3 text-sm">
          <p className="text-base font-semibold text-slate-900">
            {siteData.defaultTitle}
          </p>
          <div className="grid gap-4 md:grid-cols-3">
            {columns.map((group, index) => (
              <div key={`col-${index}`} className="space-y-2">
                {group.map((label) => (
                  <NavLink
                    key={label}
                    className="block text-slate-700 transition hover:text-amber-600"
                    to={linkMap[label] || "/"}
                  >
                    {label}
                  </NavLink>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <p className="text-base font-semibold text-slate-900">
            {siteData.defaultTitle}
          </p>
          <p className="text-sm text-slate-500">
            {siteData.defaultDescription}
          </p>
          <form className="flex flex-col gap-3">
            <input
              className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400"
              placeholder={siteData.brand?.name || "Manage Your Vastu"}
              type="email"
            />
            <button
              className="btn btn-primary w-full rounded-md"
              onClick={() =>
                logActivity("cta_click", { label: "Footer newsletter subscribe" })
              }
            >
              {siteData.defaultTitle}
            </button>
          </form>
          {/* Replace with embedded newsletter form */}
          <div className="flex flex-wrap gap-3 text-xs text-slate-500">
            <NavLink className="hover:text-amber-600" to="/privacy">
              {legalData.privacy?.title}
            </NavLink>
            <NavLink className="hover:text-amber-600" to="/terms">
              {legalData.terms?.title}
            </NavLink>
            <NavLink className="hover:text-amber-600" to="/disclaimer">
              {legalData.disclaimer?.title}
            </NavLink>
            <NavLink className="hover:text-amber-600" to="/refund-policy">
              {refundData.title}
            </NavLink>
          </div>
        </div>
      </div>
      <div className="border-t border-slate-200 py-4 text-center text-xs text-slate-500">
        {siteData.defaultTitle}
      </div>
    </footer>
  );
}
